import { Theme } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { ChartData, ChartDataset } from "chart.js";
import { t } from "i18next";
import { Stage } from "../../../shared/CaseConfiguration";
import {
  OfferStatus,
  SettlementData,
  SettlementRangeOfStage,
} from "../../../shared/CaseModel";
import { wrapLabel } from "../../../utils/chartjsTheming";
import { offerLook, statusKey } from "./offerStatus";

/** An offer on the chart: at its stage and at its amount as the party sees it. */
export interface OfferPoint {
  /** Its number in the timeline. */
  number: number;
  stage: Stage;
  /** Above zero where the party is paid, below zero where it pays. */
  amount: number;
  status: OfferStatus;
}

/** Which dataset is the point of an offer, with the number to write on it. */
export interface OfferMark {
  datasetIndex: number;
  /** The stage the point stands at, as an index of the chart's stages. */
  stageIndex: number;
  number: number;
  ink: string;
}

export type SettlementChartData = ChartData<"line", number[]>;

/**
 * How far the opponent's line and the area of the range are lightened from
 * the primary colour, and how much of its colour the area takes.
 */
const OPPONENT_LIGHTEN = 0.35;
const RANGE_LIGHTEN = 0.6;
const RANGE_ALPHA = 0.35;

/** The colours of the three lines of a range, for what names them beside the chart. */
export function rangeColors(theme: Theme): {
  party: string;
  opponent: string;
  midpoint: string;
} {
  return {
    party: theme.palette.primary.main,
    opponent: lighten(theme.palette.primary.main, OPPONENT_LIGHTEN),
    midpoint: theme.palette.text.primary,
  };
}

/** The radius of the point of an offer, which has room for its number, in px. */
const OFFER_RADIUS = 12;

/** A line shows a small point per stage, which grows under the pointer. */
const POINTS = {
  pointRadius: 2,
  pointHoverRadius: 6,
  pointHitRadius: 8,
} as const;

/** A level that holds for the whole case: a thin dotted line from end to end. */
const level = () => ({
  borderWidth: 1.5,
  borderDash: [2, 4],
  pointRadius: 0,
  pointHoverRadius: 5,
  pointHitRadius: 8,
  spanGaps: true,
  order: 3,
});

function createDataArray(value: number, length: number): number[] {
  const data = new Array(length).fill(NaN);
  data[0] = value;
  data[data.length - 1] = value;
  return data;
}

type Segment = {
  partyEvs: number[];
  opponentEvs: number[];
  midpoints: number[];
};

// a settlement changes what the two sides can expect: the lines start anew
// from the stage it is reached at
function getSegments(perStage: SettlementRangeOfStage[]): Segment[] {
  let currentSegment: Segment = {
    partyEvs: [],
    opponentEvs: [],
    midpoints: [],
  };
  const segments: Segment[] = [currentSegment];

  for (let stageIndex = 0; stageIndex < perStage.length; stageIndex++) {
    const stage = perStage[stageIndex];
    currentSegment.partyEvs.push(stage.range.partyEv);
    currentSegment.opponentEvs.push(stage.range.opponentEv);
    currentSegment.midpoints.push(stage.range.mid);
    if (stage.settlement) {
      const newSegmentStart = stage.settlement.effect;
      currentSegment = {
        partyEvs: new Array(stageIndex).fill(NaN),
        opponentEvs: new Array(stageIndex).fill(NaN),
        midpoints: new Array(stageIndex).fill(NaN),
      };
      currentSegment.partyEvs.push(newSegmentStart.partyEv);
      currentSegment.opponentEvs.push(newSegmentStart.opponentEv);
      currentSegment.midpoints.push(newSegmentStart.mid);
      segments.push(currentSegment);
    }
  }
  return segments;
}

/**
 * The bargaining range of a party against an opponent by stage, with the
 * offers between the two as numbered points.
 */
export function asChartData(
  { perStage, netResult, bestEnforcement, worstEnforcement }: SettlementData,
  partyName: string,
  opponentName: string,
  theme: Theme,
  offers: readonly OfferPoint[] = [],
): { chart: SettlementChartData; marks: OfferMark[] } {
  const names = { settlingParty: partyName, otherParty: opponentName };
  const label = (key: string) =>
    t(`case.analytics.settlement.chart.labels.${key}`, names);

  const { main: primary } = theme.palette.primary;
  const { positive, negative } = theme.palette.data;
  const opponent = lighten(primary, OPPONENT_LIGHTEN);

  const datasets: ChartDataset<"line", number[]>[] = [];
  for (const segment of getSegments(perStage)) {
    datasets.push(
      {
        label: label("evSettlingParty"),
        data: segment.partyEvs,
        borderColor: primary,
        backgroundColor: primary,
        borderWidth: 2,
        ...POINTS,
        order: 2,
      },
      {
        label: label("evOther"),
        data: segment.opponentEvs,
        fill: "-1",
        borderColor: opponent,
        backgroundColor: alpha(lighten(primary, RANGE_LIGHTEN), RANGE_ALPHA),
        borderWidth: 2,
        ...POINTS,
        order: 2,
      },
      {
        label: label("midpoint"),
        data: segment.midpoints,
        borderColor: theme.palette.text.primary,
        backgroundColor: theme.palette.text.primary,
        borderWidth: 2,
        borderDash: [7, 5],
        ...POINTS,
        order: 1,
      },
    );
  }
  datasets.push({
    label: label("netResult"),
    data: createDataArray(netResult, perStage.length),
    borderColor: theme.palette.text.secondary,
    backgroundColor: theme.palette.text.secondary,
    ...level(),
  });
  if (worstEnforcement !== undefined)
    datasets.push({
      label: label("worstEnforcement"),
      data: createDataArray(worstEnforcement, perStage.length),
      borderColor: negative.main,
      backgroundColor: negative.main,
      ...level(),
    });
  if (bestEnforcement !== undefined)
    datasets.push({
      label: label("bestEnforcement"),
      data: createDataArray(bestEnforcement, perStage.length),
      borderColor: positive.main,
      backgroundColor: positive.main,
      ...level(),
    });

  // each offer is a point of its own, so that it answers to the pointer
  const marks: OfferMark[] = [];
  for (const offer of offers) {
    const stageIndex = perStage.findIndex(({ stage }) => stage === offer.stage);
    if (stageIndex < 0) continue;
    const look = offerLook(theme, offer.status);
    marks.push({
      datasetIndex: datasets.length,
      stageIndex,
      number: offer.number,
      ink: look.markInk,
    });
    datasets.push({
      label: t("case.analytics.settlement.range.offer", {
        number: offer.number,
        status: t(
          `case.analytics.settlement.offerDialog.status.${statusKey(offer.status)}`,
        ).toLowerCase(),
      }),
      // a value at its stage alone: the stages are told by their position
      data: perStage.map((_, index) =>
        index === stageIndex ? offer.amount : NaN,
      ),
      showLine: false,
      pointRadius: OFFER_RADIUS,
      pointHoverRadius: OFFER_RADIUS + 1,
      pointHitRadius: OFFER_RADIUS,
      pointBackgroundColor: look.color,
      pointBorderColor: theme.palette.background.paper,
      pointBorderWidth: 2,
      order: 0,
    });
  }

  return {
    chart: {
      labels: perStage.map(({ stage }) =>
        wrapLabel(t("case.stages." + Stage[stage], Stage[stage])),
      ),
      datasets,
    },
    marks,
  };
}
